import {
  CloseIcon,
  Mute as MuteIcon,
  MuteIcon as MuteIconNew,
  PauseIcon,
  ResumeIcon,
  RewindIcon,
  TickIcon,
  TrashIcon,
  Volume as VolumeIcon,
  VolumeIcon as VolumeIconNew
} from '@mote/assets/icons'
import { MoteLogo } from '@mote/assets/logo'
import { isMobile, preventBubblingEvent } from '@mote/helpers/browser'
import { Block, Tooltip as StyledTooltip } from '@mote/ui'
import { Howler } from 'howler'
import React from 'react'
import styled, { css } from 'styled-components'
import { AudioAnimation } from '../hooks'
import { KeyboardKey } from '@mote/helpers/constants'

export const Control = React.forwardRef((props, ref) => {
  const {
    children,
    tooltipProps = {
      distance: 8
    },
    disabled = false,
    onClick,
    ...rest
  } = props

  const { disabled: tooltipDisabled = false, ...restTooltipProps } =
    tooltipProps

  return (
    <Button
      ref={ref}
      type='button'
      aria-disabled={disabled}
      disabled={disabled}
      onClick={event => {
        preventBubblingEvent(event)
        onClick?.(event)
      }}
      onMouseDown={event => {
        event.preventDefault()
      }}
      onKeyDown={event => {
        switch (event.keyCode) {
          case KeyboardKey.Space:
          case KeyboardKey.Enter:
            preventBubblingEvent(event)
            onClick?.(event)
            break
        }
      }}
      {...rest}
    >
      <Tooltip
        content={rest['aria-label']}
        disabled={[isMobile(), tooltipDisabled, disabled].some(
          flag => flag === true
        )}
        {...restTooltipProps}
      >
        {children}
      </Tooltip>
    </Button>
  )
})

export const StartRecordingButton = React.forwardRef((props, ref) => {
  const {
    tooltipProps: _tooltipProps = {},
    icon = <MoteLogo size={100} variant='gradient' showShadow isAnimated />,
    ...rest
  } = props

  return (
    <Control
      ref={ref}
      size={100}
      aria-label='Start recording'
      tooltipProps={{ distance: 12, content: 'Start', ..._tooltipProps }}
      {...rest}
    >
      {icon}
    </Control>
  )
})

export const CancelRecordingButton = React.forwardRef((props, ref) => {
  const { tooltipProps: _tooltipProps = {}, ...rest } = props

  return (
    <CancelControl
      ref={ref}
      size={42}
      aria-label='Cancel recording'
      tooltipProps={{ distance: 6, content: 'Cancel', ..._tooltipProps }}
      {...rest}
    >
      <CloseIcon color='currentColor' />
    </CancelControl>
  )
})

export const PauseRecordingButton = React.forwardRef((props, ref) => {
  const { tooltipProps: _tooltipProps = {}, ...rest } = props

  return (
    <Control
      ref={ref}
      size={42}
      aria-label='Pause recording'
      tooltipProps={{ distance: 6, content: 'Pause', ..._tooltipProps }}
      {...rest}
    >
      <PauseIcon color='currentColor' />
    </Control>
  )
})

export const ResumeRecordingButton = React.forwardRef((props, ref) => {
  const { tooltipProps: _tooltipProps = {}, ...rest } = props

  return (
    <Control
      ref={ref}
      size={42}
      aria-label='Resume recording'
      tooltipProps={{ distance: 6, content: 'Resume', ..._tooltipProps }}
      {...rest}
    >
      <ResumeIcon color='currentColor' />
    </Control>
  )
})

export const ResumePauseRecordingButton = React.forwardRef(
  ({ isRecording = true, tooltipProps: _tooltipProps = {}, ...rest }, ref) => {
    return (
      <Control
        ref={ref}
        size={42}
        aria-label={isRecording ? 'Pause recording' : 'Resume recording'}
        tooltipProps={{
          distance: 6,
          content: isRecording ? 'Pause' : 'Resume',
          ..._tooltipProps
        }}
        {...rest}
      >
        {isRecording && <PauseIcon color='currentColor' />}
        {!isRecording && <ResumeIcon color='currentColor' />}
      </Control>
    )
  }
)

export const CompleteRecordingButton = React.forwardRef((props, ref) => {
  const {
    backgroundColor = '#AC0AE7',
    audioStream = Howler.masterGain,
    audioContext = Howler.ctx,
    tooltipProps: _tooltipProps = {},
    icon = <TickIcon color='#ffffff' />,
    ...rest
  } = props

  return (
    <CompleteControl
      ref={ref}
      size={100}
      aria-label='Complete recording'
      tooltipProps={{ distance: 8, content: 'Complete', ..._tooltipProps }}
      backgroundColor={backgroundColor}
      {...rest}
    >
      <AudioAnimation audioContext={audioContext} audioStream={audioStream}>
        {icon}
      </AudioAnimation>
    </CompleteControl>
  )
})

export const CloseButton = React.forwardRef(({ onClick, ...props }, ref) => {
  return (
    <CloseButtonRoot
      ref={ref}
      size={24}
      aria-label='Close'
      onClick={event => {
        preventBubblingEvent(event)
        onClick?.(event)
      }}
      onMouseDown={event => {
        event.preventDefault()
      }}
      onKeyDown={event => {
        switch (event.keyCode) {
          case KeyboardKey.Space:
          case KeyboardKey.Enter:
            preventBubblingEvent(event)
            onClick?.(event)
            break
        }
      }}
      {...props}
    >
      <CloseIcon color='currentColor' />
    </CloseButtonRoot>
  )
})

export const RewindButton = React.forwardRef((props, ref) => {
  const { tooltipProps: _tooltipProps = {}, ...rest } = props

  return (
    <RewindControl
      ref={ref}
      size={24}
      aria-label='Rewind'
      tooltipProps={{
        distance: 7,
        content: 'Rewind (j)',
        direction: 'up',
        ..._tooltipProps
      }}
      {...rest}
    >
      <RewindIcon color='currentColor' />
    </RewindControl>
  )
})

export const PlayButton = React.forwardRef((props, ref) => {
  const { tooltipProps: _tooltipProps = {}, ...rest } = props

  return (
    <PlayControl
      ref={ref}
      size={36}
      aria-label='Play'
      data-testid='play-button'
      tooltipProps={{
        distance: 7,
        content: 'Play (k)',
        direction: 'up',
        ..._tooltipProps
      }}
      {...rest}
    >
      <PlayIcon color='currentColor' />
    </PlayControl>
  )
})

export const PauseButton = React.forwardRef((props, ref) => {
  const {
    isReactive = true,
    audioStream = Howler.masterGain,
    audioContext = Howler.ctx,
    tooltipProps: _tooltipProps = {},
    ...rest
  } = props

  return (
    <PauseControl
      ref={ref}
      size={36}
      aria-label='Pause'
      data-testid='pause-button'
      tooltipProps={{
        distance: 7,
        content: 'Pause (k)',
        direction: 'up',
        ..._tooltipProps
      }}
      {...rest}
    >
      {isReactive && (
        <AudioAnimation audioContext={audioContext} audioStream={audioStream}>
          <PauseIcon color='currentColor' />
        </AudioAnimation>
      )}

      {!isReactive && <PauseIcon color='currentColor' />}
    </PauseControl>
  )
})

export const PlayPauseButton = React.forwardRef(
  ({ isPlaying = false, ...props }, ref) => {
    const { isReactive, audioContext, audioStream, ...rest } =
      React.useMemo(() => {
        const {
          isReactive = true,
          audioStream = Howler.masterGain,
          audioContext = Howler.ctx,
          tooltipProps: _tooltipProps = {},
          ...rest
        } = props

        const defaultProps = {
          size: 36,
          'aria-label': isPlaying ? 'Pause' : 'Play',
          'data-testid': isPlaying ? 'pause-button' : 'play-button',
          tooltipProps: {
            distance: 7,
            content: isPlaying ? 'Pause (k)' : 'Play (k)',
            direction: 'up',
            ..._tooltipProps
          },
          ...rest
        }

        return isPlaying
          ? {
              ...defaultProps,
              isReactive,
              audioContext,
              audioStream
            }
          : defaultProps
      }, [isPlaying, props])

    return (
      <PlayPauseButtonControl
        ref={ref}
        isPlaying={isPlaying}
        isReactive={isReactive}
        {...rest}
      >
        {isPlaying && (
          <>
            {isReactive && (
              <AudioAnimation
                audioContext={audioContext}
                audioStream={audioStream}
              >
                <PauseIcon color='currentColor' />
              </AudioAnimation>
            )}

            {!isReactive && <PauseIcon color='currentColor' />}
          </>
        )}
        {!isPlaying && <PlayIcon color='currentColor' />}
      </PlayPauseButtonControl>
    )
  }
)

export const VolumeMuteButton = React.forwardRef((props, ref) => {
  const {
    isMuted,
    useNewIcon = false,
    tooltipProps: _tooltipProps = {},
    ...rest
  } = props
  const { text, icon } = React.useMemo(() => {
    const muteIcon = useNewIcon ? (
      <MuteIconNew color='currentColor' />
    ) : (
      <MuteIcon />
    )
    const volumeIcon = useNewIcon ? (
      <VolumeIconNew color='currentColor' />
    ) : (
      <VolumeIcon />
    )

    return {
      text: isMuted ? 'Unmute' : 'Mute',
      icon: isMuted ? muteIcon : volumeIcon
    }
  }, [isMuted, useNewIcon])

  return (
    <Control
      ref={ref}
      size={24}
      aria-label={text}
      tooltipProps={{
        distance: 7,
        content: `${text} (m)`,
        direction: 'up',
        ..._tooltipProps
      }}
      {...rest}
    >
      {icon}
    </Control>
  )
})

export const DeleteButton = React.forwardRef((props, ref) => {
  const { tooltipProps: _tooltipProps = {}, ...rest } = props

  return (
    <DeleteControl
      ref={ref}
      size={24}
      aria-label='Delete'
      tooltipProps={{
        distance: 7,
        content: 'Delete (d)',
        direction: 'up',
        ..._tooltipProps
      }}
      {...props}
    >
      <TrashIcon color='currentColor' />
    </DeleteControl>
  )
})

export const ControlsContainer = props => (
  <ControlsContainerRoot
    layout='horizontal'
    align='center'
    justify='center'
    gap={8}
    {...props}
  />
)

const ControlsContainerRoot = styled(Block)`
  position: relative;
`

export const Tooltip = styled(StyledTooltip)`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  .react-tooltip-lite {
    width: max-content !important;
  }
`

export const Button = styled.button`
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 50%;
  border: 0;
  padding: 0;
  appearance: none;
  user-select: none;
  background-color: transparent;
  outline: transparent solid 2px !important;
  outline-offset: 2px;
  transition: all 150ms ease-in;
  box-sizing: border-box;
  -webkit-tap-highlight-color: transparent;

  svg {
    height: 100%;
    width: 100%;
  }

  ${({ variant }) => {
    switch (variant) {
      case 'rich':
        return css`
          color: #ffffff;

          &:hover,
          &:focus {
            background: rgba(33, 34, 40, 0.2);
          }

          &:focus {
            box-shadow: 0 0 0 2px rgba(47, 166, 255, 0.6);
          }
        `
      default:
        return css`
          color: #d1d1e0;

          &:focus {
            box-shadow: 0 0 0 3px rgba(172, 10, 231, 0.4);
          }

          &:hover,
          &:focus {
            color: #232323;
          }
        `
    }
  }};

  ${({ size }) =>
    size !== undefined &&
    css`
      width: ${size}px;
      height: ${size}px;
    `};

  ${({ disabled }) =>
    disabled &&
    css`
      cursor: not-allowed;
      opacity: 0.4;
    `};
`

const CancelControl = styled(Control)`
  &:hover,
  &:focus {
    color: #f44336;
  }
`

const CompleteControl = styled(Control)`
  background: ${({ backgroundColor }) => backgroundColor};

  svg {
    height: 42px;
    width: 42px;
  }

  span[class^='reactive-audio'] {
    width: 100%;
    height: 100%;
  }

  span.reactive-audio__content {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
`

const PlaybackControl = styled(Control)`
  ${({ variant }) => {
    switch (variant) {
      case 'rich':
        return css`
          color: #ffffff;

          &:hover,
          &:focus {
            color: #ffffff;
          }
        `
      default:
        return css`
          color: #ac0ae7;
          border: 1px solid #ececf3;

          &:hover,
          &:focus {
            color: #ac0ae7;
          }
        `
    }
  }};

  ${({ isReactive }) => isReactive && 'box-shadow: none !important;'};
`

const DeleteControl = styled(PlaybackControl)`
  ${({ size }) =>
    size > 24 &&
    css`
      svg {
        height: 24px;
        width: 24px;
      }
    `}
`

const RewindControl = styled(PlaybackControl)`
  ${({ size }) =>
    size > 24 &&
    css`
      svg {
        height: 24px;
        width: 24px;
      }
    `}
`

const CloseButtonRoot = styled(Button)`
  color: #232323;
  border-radius: 4px;

  &:hover,
  &:focus {
    color: #ac0ae8;
    background: #f6f6f9;
  }
`

const PlayControl = styled(PlaybackControl)`
  z-index: 1;
`

const pauseButtonStyles = size => css`
  svg {
    height: ${size > 36 ? 32 : size > 24 ? 24 : 16}px;
    width: ${size > 36 ? 32 : size > 24 ? 24 : 16}px;
  }

  span[class^='reactive-audio'] {
    width: 100%;
    height: 100%;
  }

  span.reactive-audio__content {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
  }
`

const PauseControl = styled(PlaybackControl)`
  z-index: 1;

  ${pauseButtonStyles}
`

const PlayPauseButtonControl = styled(PlaybackControl)`
  z-index: 1;

  ${({ isPlaying, size }) => isPlaying && pauseButtonStyles(size)}
`

const PlayIcon = ({ color = '#AC0AE8', ...rest }) => (
  <svg
    width='36'
    height='36'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    viewBox='0 0 36 36'
    {...rest}
    focusable={false}
    aria-hidden={true}
    role='presentation'
  >
    <path
      d='M23.827 16.9555L14.7199 11.7357C13.9696 11.3091 12.8571 11.7357 12.8571 12.7646V23.2041C12.8571 24.1577 13.892 24.7349 14.7199 24.2581L23.827 19.0383C24.629 18.5615 24.629 17.4323 23.827 16.9555Z'
      fill={color}
    />
  </svg>
)
